import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInicisPayment } from '../../payment/hooks/useInicisPayment';
import { useAddUserIdentity } from '../../../domains/user/ui/hooks/useAddUserIdentity';
import type {
  addUserIdentityBody,
  addUserIdentityResult
} from '../../../domains/user/services/userIdentities';

const hasNestedError = (e: unknown): e is { error: { code: string; message: string } } => {
  return typeof e === 'object' && e !== null && 'error' in e;
};

export function useIdentityVerificationRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentQueryParams = new URLSearchParams(location.search);
  const redirectPath = currentQueryParams.get('redirect_path');
  const impUid = currentQueryParams.get('imp_uid');
  const merchantUid = currentQueryParams.get('merchant_uid');
  const initialSuccess = currentQueryParams.get('success') === 'true';

  const [isSuccess, setIsSuccess] = useState(initialSuccess);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const { amount, productName, productId, isBilling, type } = (() => {
    const topAmount = currentQueryParams.get('amount');
    const topProductName = currentQueryParams.get('productName');
    const topProductId = currentQueryParams.get('productId');
    const topIsBilling = currentQueryParams.get('isBilling');
    const topType = currentQueryParams.get('type');

    if (topAmount || topProductName || topProductId || topType) {
      return {
        amount: topAmount,
        productName: topProductName,
        productId: topProductId,
        isBilling: topIsBilling === 'true',
        type: topType
      };
    }

    if (redirectPath) {
      const decoded = decodeURIComponent(redirectPath);
      const url = new URL(decoded, window.location.origin);
      const sp = url.searchParams;

      return {
        amount: sp.get('amount'),
        productName: sp.get('productName'),
        productId: sp.get('productId'),
        isBilling: sp.get('isBilling') === 'true',
        type: sp.get('type')
      };
    }

    return { amount: null, productName: null, productId: null, type: null };
  })();
  
  const { mutateAsync: addUserIdentity } = useAddUserIdentity();

  const { handleCreateOrder } = useInicisPayment({
    amount: amount ?? '',
    productName: productName ?? '',
    productId: productId ?? ''
  });

  useEffect(() => {
    if (isSuccess && impUid && merchantUid) {
      (async () => {
        try {
          const { data, error }: addUserIdentityResult = await addUserIdentity({
            imp_uid: impUid,
            merchant_uid: merchantUid
          } as addUserIdentityBody);
          if (error) {
            setIsSuccess(false);
            const nestedCode = hasNestedError(error) ? error.error.code : undefined;
            const nestedMessage = hasNestedError(error) ? error.error.message : undefined;

            if (nestedCode === '1002') {
              setIsDuplicate(true);
            }
            if (
              nestedCode === 'RecordNotUnique' &&
              nestedMessage === 'This key already identified'
            ) {
              setIsDuplicate(true);
            }
            return;
          }
          if (data?.userIdentity?.userIdentityId) {
            setIsSuccess(true);
          }
        } catch {
          setIsSuccess(false);
        }
      })();
    }
  }, [isSuccess, impUid, merchantUid, addUserIdentity]);

  const hasRequiredParams = Boolean(impUid && merchantUid);

  const handleClick = useCallback(async () => {
    if (isSuccess) {
      if (type === 'invite') {
        navigate('/free-charge/invite', { replace: true });
        return;
      }

      if (isBilling) {
        navigate(
          `/payment/billing?amount=${amount}&productName=${productName}&productId=${productId}`
        );
        return;
      }
      await handleCreateOrder();
    } else {
      navigate(redirectPath ? decodeURIComponent(redirectPath) : '/bettie-chat-list', {
        replace: true
      });
    }
  }, [
    isSuccess,
    handleCreateOrder,
    navigate,
    redirectPath,
    amount,
    productName,
    productId,
    isBilling,
    type
  ]);

  return {
    isSuccess,
    hasRequiredParams,
    handleClick,
    isDuplicate
  };
}
